import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendContactMail } from "./mailService";

const FB_PAGE_ID  = "61554849740185";
const FB_PAGE_URL = `https://www.facebook.com/profile.php?id=${FB_PAGE_ID}`;
const SITE_URL    = "https://virtualkalvi.com";
const SITE_NAME   = "VirtualKalvi";

// ─────────────────────────────────────────────────────────────
// REDESIGNED INLINE CSS
// ─────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;700;800&family=Noto+Sans+Tamil:wght@400;600;700&display=swap');

  :root {
    --navy:   #1a2e4a;
    --blue:   #2563eb;
    --teal:   #0d9488;
    --coral:  #e55c3b;
    --amber:  #d97706;
    --violet: #7c3aed;
    --green:  #16a34a;
    --bg:     #f1f5f9;
    --surface:#ffffff;
    --border: #e2e8f0;
    --text1:  #0f172a;
    --text2:  #475569;
    --text3:  #94a3b8;
    --radius: 18px;
    --shadow: 0 4px 24px rgba(15,23,42,0.08);
    --shadow-hover: 0 12px 36px rgba(15,23,42,0.14);
    --transition: all 0.2s cubic-bezier(0.4,0,0.2,1);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body { background: var(--bg); }

  /* ── ROOT ── */
  .vk-root {
    font-family: 'Baloo 2', sans-serif;
    max-width: 960px;
    margin: 0 auto;
    padding: 0 12px 80px;
    background: var(--bg);
    min-height: 100vh;
  }

  /* ══════════════════════════════════════════
     HERO BANNER
  ══════════════════════════════════════════ */
  .vk-hero {
    position: relative;
    background: linear-gradient(130deg, #1a2e4a 0%, #1e4080 50%, #1a6b8a 100%);
    border-radius: 0 0 32px 32px;
    padding: 24px 20px 28px;
    margin: 0 -12px 20px;
    overflow: hidden;
  }
  .vk-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.07) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 40%);
    pointer-events: none;
  }
  .vk-hero-wave {
    position: absolute;
    bottom: -1px; left: 0; right: 0;
    height: 40px; overflow: hidden;
  }
  .vk-hero-wave svg { width: 100%; height: 100%; }

  /* top row */
  .vk-hero-top {
    position: relative; z-index: 1;
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 16px;
  }
  .vk-logo {
    display: flex; align-items: center; gap: 10px;
  }
  .vk-logo-icon {
    width: 40px; height: 40px; border-radius: 12px;
    background: rgba(255,255,255,0.18);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
  }
  .vk-logo-text {
    font-size: 1.2857em; font-weight: 800; color: #fff;
    letter-spacing: -0.3px;
  }
  .vk-logo-sub {
    font-size: 0.7143em; color: rgba(255,255,255,0.7);
    font-weight: 600; margin-top: -2px;
  }
  .vk-hero-date-pill {
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 50px;
    padding: 5px 14px;
    font-size: 0.7857em; font-weight: 700; color: #fff;
  }

  /* hero body */
  .vk-hero-body { position: relative; z-index: 1; }
  .vk-hero-tagline {
    font-size: 1.4286em; font-weight: 800; color: #fff;
    margin-bottom: 4px; line-height: 1.25;
    font-family: 'Noto Sans Tamil', 'Baloo 2', sans-serif;
  }
  .vk-hero-sub {
    font-size: 0.8571em; color: rgba(255,255,255,0.75);
    margin-bottom: 2px;
  }

  /* stats row */
  .vk-stats-row {
    display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap;
  }
  .vk-stat {
    flex: 1; min-width: 80px;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(6px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 14px;
    padding: 10px 14px;
    text-align: center;
  }
  .vk-stat-num {
    font-size: 1.4286em; font-weight: 800; color: #fff; line-height: 1;
  }
  .vk-stat-lbl {
    font-size: 0.7143em; color: rgba(255,255,255,0.75); font-weight: 600;
    margin-top: 3px;
  }

  /* ══════════════════════════════════════════
     ABOUT / DESCRIPTION SECTION
  ══════════════════════════════════════════ */
  .vk-about {
    background: var(--surface); border-radius: 20px;
    padding: 20px 20px 18px; margin-bottom: 16px;
    box-shadow: var(--shadow);
    border-left: 5px solid #2563eb;
  }
  .vk-about-ta {
    font-family: 'Noto Sans Tamil', sans-serif;
    font-size: 1em; font-weight: 600; color: #1a2e4a;
    line-height: 1.8; margin-bottom: 12px;
  }
  .vk-about-en {
    font-size: 0.88em; color: var(--text2); line-height: 1.7;
    border-top: 1px solid var(--border); padding-top: 10px;
  }

  /* ══════════════════════════════════════════
     FEATURE CARDS
  ══════════════════════════════════════════ */
  .vk-features-title {
    font-size: 1.05em; font-weight: 800; color: var(--text1);
    margin-bottom: 12px; display: flex; align-items: center; gap: 8px;
    font-family: 'Noto Sans Tamil', 'Baloo 2', sans-serif;
  }
  .vk-features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px; margin-bottom: 20px;
  }
  .vk-feat-card {
    background: var(--surface); border-radius: 16px;
    padding: 16px; box-shadow: var(--shadow);
    border-top: 4px solid transparent;
    transition: var(--transition);
    cursor: default;
  }
  .vk-feat-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-hover); }
  .vk-feat-icon {
    font-size: 2em; margin-bottom: 10px;
    width: 52px; height: 52px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
  }
  .vk-feat-ta {
    font-family: 'Noto Sans Tamil', sans-serif;
    font-size: 0.95em; font-weight: 700; color: var(--text1);
    margin-bottom: 4px; line-height: 1.5;
  }
  .vk-feat-en {
    font-size: 0.78em; color: var(--text2); line-height: 1.6;
  }
  .vk-feat-badge {
    display: inline-block; margin-top: 8px;
    font-size: 0.7em; font-weight: 700; border-radius: 50px;
    padding: 2px 10px;
  }

  /* ══════════════════════════════════════════
     STUDENT PICTURES SECTION
  ══════════════════════════════════════════ */
  .vk-students-wrap {
    background: linear-gradient(135deg, #1a2e4a 0%, #1e4080 100%);
    border-radius: 20px; padding: 20px 16px 24px;
    margin-bottom: 16px; overflow: hidden; position: relative;
  }
  .vk-students-wrap::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255,255,255,0.06) 0%, transparent 50%);
    pointer-events: none;
  }
  .vk-students-title {
    font-family: 'Noto Sans Tamil', 'Baloo 2', sans-serif;
    font-size: 1.05em; font-weight: 800; color: #fff;
    text-align: center; margin-bottom: 16px;
  }
  .vk-students-title span { color: #fbbf24; }
  .vk-students-row {
    display: flex; gap: 14px; justify-content: center;
    flex-wrap: wrap;
  }
  .vk-student-card {
    display: flex; flex-direction: column; align-items: center;
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 18px; padding: 14px 12px 12px;
    width: 140px; transition: var(--transition);
  }
  .vk-student-card:hover { transform: translateY(-4px); background: rgba(255,255,255,0.16); }
  .vk-student-img {
    width: 90px; height: 90px; border-radius: 50%;
    object-fit: cover; object-position: top;
    border: 3px solid rgba(255,255,255,0.4);
    margin-bottom: 10px;
    background: rgba(255,255,255,0.1);
  }
  .vk-student-name {
    font-family: 'Noto Sans Tamil', 'Baloo 2', sans-serif;
    font-size: 0.8em; font-weight: 700; color: #fff;
    text-align: center; margin-bottom: 4px;
  }
  .vk-student-quote {
    font-family: 'Noto Sans Tamil', sans-serif;
    font-size: 0.7em; color: rgba(255,255,255,0.8);
    text-align: center; line-height: 1.5;
  }

  /* ══════════════════════════════════════════
     SOCIAL STRIP
  ══════════════════════════════════════════ */
  .vk-social-strip {
    display: flex; align-items: center; gap: 12px;
    background: var(--surface); border-radius: 16px;
    padding: 12px 16px; margin-bottom: 14px;
    box-shadow: var(--shadow); flex-wrap: wrap;
    border-left: 4px solid #1877F2;
  }
  .vk-social-icon {
    width: 40px; height: 40px; border-radius: 12px;
    background: #1877F2;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .vk-social-text { flex: 1; min-width: 0; }
  .vk-social-title { font-size: 0.9286em; font-weight: 800; color: #1877F2; }
  .vk-social-sub   { font-size: 0.7857em; color: var(--text2); margin-top: 1px; }
  .vk-btn-fb {
    background: #1877F2; color: #fff; border: none; border-radius: 50px;
    padding: 7px 16px; font-size: 0.8571em; font-weight: 700;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    transition: var(--transition); font-family: 'Baloo 2', sans-serif;
    white-space: nowrap;
  }
  .vk-btn-fb:hover { background: #1565c0; transform: scale(1.04); }
  .vk-btn-msg {
    background: linear-gradient(135deg, #0084ff, #00c6ff);
    color: #fff; border: none; border-radius: 50px;
    padding: 7px 16px; font-size: 0.8571em; font-weight: 700;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    transition: var(--transition); font-family: 'Baloo 2', sans-serif;
    white-space: nowrap;
  }
  .vk-btn-msg:hover { opacity: 0.88; transform: scale(1.04); }

  /* ══════════════════════════════════════════
     TOOLBAR
  ══════════════════════════════════════════ */
  .vk-toolbar {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 16px; flex-wrap: wrap;
  }
  .vk-tb-btn {
    border: none; border-radius: 50px; padding: 7px 16px;
    font-size: 0.8571em; font-weight: 700;
    cursor: pointer; transition: var(--transition);
    font-family: 'Baloo 2', sans-serif;
    display: flex; align-items: center; gap: 5px;
  }
  .vk-tb-btn:hover { transform: scale(1.05); }

  /* view toggle group */
  .vk-view-group {
    display: flex; gap: 2px; margin-left: auto;
    background: #dde6f0; border-radius: 50px; padding: 3px;
  }
  .vk-view-btn {
    border: none; background: transparent; border-radius: 50px;
    padding: 6px 14px; font-size: 0.8571em; font-weight: 700;
    cursor: pointer; color: var(--navy);
    font-family: 'Baloo 2', sans-serif; transition: var(--transition);
    display: flex; align-items: center; gap: 4px; white-space: nowrap;
  }
  .vk-view-btn.active {
    background: var(--navy); color: #fff;
    box-shadow: 0 2px 8px rgba(26,46,74,0.3);
  }
  .vk-view-btn:not(.active):hover { background: rgba(26,46,74,0.10); }

  /* ══════════════════════════════════════════
     GRADE TAB STRIP
  ══════════════════════════════════════════ */
  .vk-tabs-wrap {
    position: sticky; top: 0; z-index: 50;
    background: var(--surface);
    border-bottom: 1.5px solid var(--border);
    margin: 0 -12px 16px;
    padding: 0 12px;
    box-shadow: 0 2px 10px rgba(26,46,74,0.06);
  }
  .vk-tabs {
    display: flex; gap: 0; overflow-x: auto;
    scrollbar-width: none; -ms-overflow-style: none;
  }
  .vk-tabs::-webkit-scrollbar { display: none; }
  .vk-tab {
    flex-shrink: 0; border: none; background: transparent;
    font-family: 'Baloo 2', sans-serif; font-size: 0.8571em; font-weight: 700;
    padding: 12px 18px 10px; cursor: pointer; color: var(--text2);
    border-bottom: 3px solid transparent;
    transition: var(--transition); white-space: nowrap;
  }
  .vk-tab:hover { color: var(--navy); background: #f1f5f9; }
  .vk-tab.active {
    color: var(--navy); border-bottom-color: var(--blue); font-weight: 800;
  }

  /* ══════════════════════════════════════════
     SECTION (per grade)
  ══════════════════════════════════════════ */
  .vk-section {
    background: var(--surface);
    border-radius: var(--radius);
    margin-bottom: 18px;
    box-shadow: var(--shadow);
    overflow: hidden;
    border: 1px solid var(--border);
  }
  .vk-section-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px 12px;
    border-bottom: 1.5px solid var(--border);
  }
  .vk-section-title {
    font-size: 1.0714em; font-weight: 800; color: var(--text1);
    display: flex; align-items: center; gap: 8px;
  }
  .vk-section-dot {
    width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
  }
  .vk-section-badge {
    font-size: 0.7143em; font-weight: 800;
    padding: 3px 12px; border-radius: 50px; color: #fff; flex-shrink: 0;
  }

  /* section search */
  .vk-section-search-wrap { padding: 10px 16px 0; }
  .vk-section-search {
    width: 100%; border: 1.5px solid var(--border);
    border-radius: 50px; padding: 8px 16px;
    font-size: 0.9286em; outline: none;
    background: #f8fafc; font-family: 'Baloo 2', sans-serif;
    transition: var(--transition); color: var(--text1);
  }
  .vk-section-search:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
    background: #fff;
  }
  .vk-section-empty {
    padding: 20px 18px; color: var(--text2);
    font-size: 0.9286em; font-weight: 700; text-align: center;
  }

  /* ── LIST VIEW ── */
  .vk-list-links { padding: 4px 0; }
  .vk-list-row {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 18px; cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.15s;
  }
  .vk-list-row:last-child { border-bottom: none; }
  .vk-list-row:hover { background: #f8fafc; }
  .vk-list-icon {
    width: 40px; height: 40px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; flex-shrink: 0;
  }
  .vk-list-body { flex: 1; min-width: 0; }
  .vk-list-title {
    font-size: 0.9286em; font-weight: 800; color: var(--text1);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .vk-list-sub {
    font-size: 0.7857em; color: var(--text2);
    margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .vk-list-meta { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .vk-teacher-chip {
    display: flex; align-items: center; gap: 5px;
  }
  .vk-teacher-chip img {
    width: 26px; height: 26px; border-radius: 50%;
    object-fit: cover; border: 2px solid var(--border);
  }
  .vk-teacher-chip-name {
    font-size: 0.7857em; font-weight: 700; color: var(--blue);
    max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .vk-time-tag { font-size: 0.7143em; color: var(--text3); font-weight: 600; }
  .vk-list-arrow { font-size: 1.2857em; color: var(--text3); }

  /* ── GRID VIEW ── */
  .vk-grid-wrap {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 12px; padding: 12px 16px 16px;
  }
  .vk-grid-card {
    border-radius: 14px; padding: 14px 14px 12px;
    cursor: pointer; transition: var(--transition);
    border-top: 3px solid transparent;
    display: flex; flex-direction: column; gap: 6px;
    background: #f8fafc; border: 1px solid var(--border);
  }
  .vk-grid-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-hover); background: #fff; }
  .vk-grid-icon { font-size: 24px; }
  .vk-grid-title { font-size: 0.9286em; font-weight: 800; color: var(--text1); line-height: 1.3; }
  .vk-grid-sub   { font-size: 0.7143em; color: var(--text2); line-height: 1.4; }
  .vk-grid-teacher {
    font-size: 0.7143em; font-weight: 700; color: var(--blue);
    margin-top: 4px; display: flex; align-items: center; gap: 4px;
  }
  .vk-grid-teacher img { width: 18px; height: 18px; border-radius: 50%; object-fit: cover; }

  /* ── CARDS VIEW ── */
  .vk-cards-wrap {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 12px; padding: 12px 16px 16px;
  }
  .vk-card {
    border-radius: 14px; overflow: hidden; cursor: pointer;
    box-shadow: var(--shadow); transition: var(--transition);
    background: var(--surface); border: 1px solid var(--border);
    border-top: 3px solid transparent;
  }
  .vk-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-hover); }
  .vk-card-top {
    padding: 14px 14px 10px;
    display: flex; gap: 10px; align-items: flex-start;
  }
  .vk-card-icon {
    width: 44px; height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; flex-shrink: 0;
  }
  .vk-card-title { font-size: 0.9286em; font-weight: 800; color: var(--text1); line-height: 1.3; }
  .vk-card-sub   { font-size: 0.7857em; color: var(--text2); margin-top: 2px; line-height: 1.4; }
  .vk-card-footer {
    padding: 8px 14px;
    border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .vk-card-teacher { display: flex; align-items: center; gap: 5px; }
  .vk-card-teacher img {
    width: 22px; height: 22px; border-radius: 50%;
    object-fit: cover; border: 2px solid var(--border);
  }
  .vk-card-teacher-name { font-size: 0.7857em; font-weight: 700; color: var(--blue); }

  /* ── Share button (on card) ── */
  .vk-share-icon-btn {
    background: none; border: none; cursor: pointer;
    font-size: 18px; padding: 4px 6px;
    border-radius: 8px; line-height: 1;
    /* iOS: minimum tap target 44px */
    min-width: 44px; min-height: 44px;
    display: flex; align-items: center; justify-content: center;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: background 0.15s;
    color: var(--text2);
  }
  .vk-share-icon-btn:hover { background: #f1f5f9; }

  /* iOS share sheet — ensure popup buttons are tappable */
  .vk-share-btn, .vk-btn-fb, .vk-btn-msg, .vk-tb-btn, .vk-view-btn, .vk-tab {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  /* ══════════════════════════════════════════
     SHARE POPUP (bottom sheet)
  ══════════════════════════════════════════ */
  .vk-share-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.55);
    display: flex; align-items: flex-end; justify-content: center;
    z-index: 2000; padding: 0;
  }
  .vk-share-box {
    background: #fff; border-radius: 22px 22px 0 0;
    padding: 20px; width: 100%; max-width: 500px;
    animation: slideUp .3s cubic-bezier(.34,1.56,.64,1);
  }
  @keyframes slideUp { from{transform:translateY(100%);opacity:0} to{transform:none;opacity:1} }
  .vk-share-title { font-weight: 800; font-size: 1.0714em; color: var(--text1); margin-bottom: 12px; }
  .vk-share-preview {
    background: #f8fafc; border-radius: 12px; padding: 10px 14px;
    margin-bottom: 14px; font-size: 0.8571em; color: var(--text1); line-height: 1.6;
    border: 1px solid var(--border);
  }
  .vk-share-btns { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
  .vk-share-btn {
    border: none; border-radius: 14px; padding: 14px 8px;
    cursor: pointer; font-size: 0.8571em; font-weight: 700;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    transition: var(--transition); font-family: 'Baloo 2', sans-serif;
    background: #f1f5f9; color: var(--text1);
  }
  .vk-share-btn:hover { transform: scale(1.05); }
  .vk-share-btn-icon { font-size: 26px; }

  /* ══════════════════════════════════════════
     CONTACT MODAL
  ══════════════════════════════════════════ */
  .vk-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 2000; padding: 16px;
  }
  .vk-modal {
    background: #fff; border-radius: 20px; padding: 24px;
    width: 100%; max-width: 500px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.18);
    animation: popIn .25s cubic-bezier(.34,1.56,.64,1);
  }
  @keyframes popIn { from{transform:scale(.88);opacity:0} to{transform:none;opacity:1} }
  .vk-modal-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 16px;
  }
  .vk-modal-title { font-size: 1.0714em; font-weight: 800; color: var(--navy); }
  .vk-modal-close {
    background: #f1f5f9; border: none; width: 32px; height: 32px;
    border-radius: 50%; font-size: 16px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: var(--transition);
  }
  .vk-modal-close:hover { background: #e2e8f0; }
  .vk-input {
    width: 100%; border-radius: 12px; border: 1.5px solid var(--border);
    padding: 10px 14px; margin-bottom: 10px;
    font-size: 0.9286em; font-family: 'Baloo 2', sans-serif;
    outline: none; transition: var(--transition); color: var(--text1);
  }
  .vk-input:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
  .vk-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }
  .vk-btn-cancel {
    background: #f1f5f9; border: none; border-radius: 50px; padding: 9px 20px;
    font-weight: 700; cursor: pointer; font-family: 'Baloo 2', sans-serif;
    color: var(--text2); transition: var(--transition);
  }
  .vk-btn-cancel:hover { background: #e2e8f0; }
  .vk-btn-send {
    background: linear-gradient(135deg, var(--navy), var(--blue));
    color: #fff; border: none; border-radius: 50px; padding: 9px 24px;
    font-weight: 700; cursor: pointer; font-family: 'Baloo 2', sans-serif;
    transition: var(--transition);
  }
  .vk-btn-send:hover { opacity: 0.9; transform: scale(1.03); }

  /* ══════════════════════════════════════════
     FOOTER
  ══════════════════════════════════════════ */
  .vk-footer {
    background: var(--navy); color: #94a3b8;
    padding: 16px 20px; margin-top: 24px;
    border-radius: 16px; text-align: center;
    font-size: 0.8571em; font-weight: 600;
  }
  .vk-footer-link {
    color: #60a5fa; cursor: pointer; text-decoration: none;
  }
  .vk-footer-link:hover { text-decoration: underline; }

  /* ══════════════════════════════════════════
     DARK MODE
  ══════════════════════════════════════════ */
  .vk-dark {
    --bg: #0d1525;
    --surface: #111d30;
    --border: #1e3050;
    --text1: #c8d8ec;
    --text2: #7a9ab5;
    --text3: #4a6a85;
  }
  .vk-dark .vk-root { background: var(--bg); }
  .vk-dark .vk-section-search { background: #0d1525; border-color: #1e3050; color: #c8d8ec; }
  .vk-dark .vk-list-row:hover { background: #1e3050; }
  .vk-dark .vk-grid-card { background: #1e3050; border-color: #1e3050; }
  .vk-dark .vk-grid-card:hover { background: #243860; }
  .vk-dark .vk-card { background: #111d30; border-color: #1e3050; }
  .vk-dark .vk-card-footer { border-top-color: #1e3050; }
  .vk-dark .vk-social-strip { background: #111d30; }
  .vk-dark .vk-tabs-wrap { background: #111d30; border-bottom-color: #1e3050; }
  .vk-dark .vk-view-group { background: #1e3050; }
  .vk-dark .vk-view-btn { color: var(--text2); }
  .vk-dark .vk-view-btn.active { background: var(--navy); }

  /* ── Loading / Empty ── */
  .vk-loading {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    min-height: 300px; gap: 14px; color: var(--navy); font-weight: 700;
  }
  .vk-spinner {
    width: 44px; height: 44px;
    border: 4px solid #e2e8f0; border-top-color: var(--blue);
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin  { to { transform: rotate(360deg); } }
  .vk-empty { text-align: center; padding: 40px 20px; color: var(--text2); font-size: 1.0714em; font-weight: 700; }
`;

// ── Subject icon map ─────────────────────────
const SUBJECT_ICONS = {
  science:   { emoji: "🔬", bg: "#dcfce7", color: "#16a34a" },
  math:      { emoji: "📐", bg: "#ede9fe", color: "#7c3aed" },
  maths:     { emoji: "➗", bg: "#ede9fe", color: "#7c3aed" },
  english:   { emoji: "📖", bg: "#dbeafe", color: "#2563eb" },
  tamil:     { emoji: "🔤", bg: "#fef3c7", color: "#d97706" },
  social:    { emoji: "🌍", bg: "#e0f2fe", color: "#0369a1" },
  history:   { emoji: "🏛️", bg: "#f3e8ff", color: "#9333ea" },
  geography: { emoji: "🗺️", bg: "#cffafe", color: "#0e7490" },
  biology:   { emoji: "🧬", bg: "#dcfce7", color: "#15803d" },
  physics:   { emoji: "⚛️", bg: "#dbeafe", color: "#1d4ed8" },
  chemistry: { emoji: "⚗️", bg: "#fce7f3", color: "#be185d" },
  computer:  { emoji: "💻", bg: "#e0e7ff", color: "#4338ca" },
  default:   { emoji: "📚", bg: "#f1f5f9", color: "#475569" },
};
const getSubjIcon = (name = "") => {
  const k = name.toLowerCase();
  return Object.entries(SUBJECT_ICONS).find(([key]) => k.includes(key))?.[1]
    || SUBJECT_ICONS.default;
};

// Accent palette (per grade)
const ACCENTS = ["#2563eb","#0d9488","#7c3aed","#d97706","#e55c3b","#16a34a","#0369a1","#be185d"];
const getAccent = (i) => ACCENTS[i % ACCENTS.length];

// ─────────────────────────────────────────────
export default function VKTemplate({ api, rows }) {
  const navigate = useNavigate();

  const [loading,      setLoading]      = useState(true);
  const [darkMode,     setDarkMode]     = useState(false);
  const [fontSize,     setFontSize]     = useState(16);
  const [showContact,  setShowContact]  = useState(false);
  const [mailSubject,  setMailSubject]  = useState("");
  const [mailBody,     setMailBody]     = useState("");
  const [shareRow,     setShareRow]     = useState(null);
  const [view,         setView]         = useState("cards"); // "list" | "grid" | "cards"
  const [activeGrade,  setActiveGrade]  = useState("All");
  const [sectionSearch,setSectionSearch]= useState({});

  useEffect(() => {
    if (!rows || rows.length === 0) { setLoading(false); return; }
    setLoading(false);
  }, [rows]);

  const handleSendMail = async () => {
    if (!mailSubject.trim() || !mailBody.trim()) return alert("Please fill all fields");
    const res = await sendContactMail(mailSubject, mailBody);
    alert(res.message);
    if (res.success) { setShowContact(false); setMailSubject(""); setMailBody(""); }
  };

  // ── Share helpers ────────────────────────────────────────
  const buildShareText = (row) =>
    `📚 ${SITE_NAME} | ${row.grade_text} — ${row.subject_name}\n` +
    `${row.subtitle}\n👩‍🏫 ${row.teacher_name}\n\n🌐 ${SITE_URL}`;

  const doShare = (row, platform) => {
    const text = buildShareText(row);
    if (platform === "wa") {
      window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
      setShareRow(null);
    } else if (platform === "fb") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}&quote=${encodeURIComponent(text)}`,
        "_blank", "width=600,height=500"
      );
      setShareRow(null);
    } else if (platform === "ig") {
      // Copy text then open Instagram
      navigator.clipboard?.writeText(text).catch(() => {});
      window.open("https://www.instagram.com/", "_blank");
      setShareRow(null);
    } else if (platform === "native") {
      // iOS requires navigator.share called SYNCHRONOUSLY inside user gesture.
      // Do NOT await anything before this call.
      if (navigator.share) {
        navigator.share({
          title: `${row.grade_text} — ${row.subject_name}`,
          text,
          url: SITE_URL,
        }).catch(e => {
          if (e.name !== "AbortError") console.warn("Share error:", e);
        });
      } else {
        // Fallback: WhatsApp on devices without share API
        window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
      }
      setShareRow(null);
    }
  };

  // ── Derived ─────────────────────────────────────────────
  const grades    = ["All", ...[...new Set((rows || []).map(r => r.grade_text))]];
  const now       = new Date();
  const months    = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const tamilDay  = ["ஞாயிறு","திங்கள்","செவ்வாய்","புதன்","வியாழன்","வெள்ளி","சனி"][now.getDay()];
  const dateStr   = `${String(now.getDate()).padStart(2,"0")} ${months[now.getMonth()]} ${now.getFullYear()}`;

  // grades to render based on active tab
  const renderGrades = activeGrade === "All"
    ? grades.slice(1)
    : [activeGrade];

  if (loading) return (
    <><style>{CSS}</style>
      <div className="vk-loading"><div className="vk-spinner"/>Loading…</div>
    </>
  );

  // ── Per-grade section ────────────────────────────────────
  const renderSection = (grade, gradeIdx) => {
    const q = (sectionSearch[grade] || "").toLowerCase();
    const items = (rows || []).filter(r =>
      r.grade_text === grade &&
      (!q || r.subject_name.toLowerCase().includes(q) ||
             r.teacher_name.toLowerCase().includes(q) ||
             (r.subtitle || "").toLowerCase().includes(q))
    );
    const accent = getAccent(grades.slice(1).indexOf(grade));

    return (
      <div key={grade} className="vk-section">

        {/* Section header */}
        <div className="vk-section-header" style={{ borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: "solid" }}>
          <div className="vk-section-title">
            <span className="vk-section-dot" style={{ background: accent }} />
            {grade}
          </div>
          <span className="vk-section-badge" style={{ background: accent }}>
            {items.length} subject{items.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Section search */}
        <div className="vk-section-search-wrap">
          <input
            className="vk-section-search"
            type="text"
            placeholder={`🔍 Search in ${grade}…`}
            value={sectionSearch[grade] || ""}
            onChange={e => setSectionSearch(prev => ({ ...prev, [grade]: e.target.value }))}
          />
        </div>

        {/* Render items by view mode */}
        {items.length === 0 ? (
          <div className="vk-section-empty">😕 No results found.</div>
        ) : view === "list" ? (

          /* ── LIST ── */
          <div className="vk-list-links">
            {items.map((row, i) => {
              const si = getSubjIcon(row.subject_name);
              return (
                <div key={i} className="vk-list-row"
                  onClick={() => navigate(`${row.link}?api=${encodeURIComponent(row.api_link)}`)}>
                  <div className="vk-list-icon" style={{ background: si.bg }}>{si.emoji}</div>
                  <div className="vk-list-body">
                    <div className="vk-list-title" style={{ color: accent }}>{row.subject_name}</div>
                    <div className="vk-list-sub">{row.subtitle}</div>
                  </div>
                  <div className="vk-list-meta">
                    <div className="vk-teacher-chip">
                      <img src={`/resources/${row.teacher_picture}`} alt={row.teacher_name}
                        onError={e => { e.target.style.display = "none"; }} />
                      <span className="vk-teacher-chip-name">{row.teacher_name}</span>
                    </div>
                    <span className="vk-time-tag">🕐 {row.updated_ago}</span>
                    <button className="vk-share-icon-btn"
                      title="Share"
                      onClick={e => { e.stopPropagation(); setShareRow(row); }}>
                      📤
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

        ) : view === "grid" ? (

          /* ── GRID ── */
          <div className="vk-grid-wrap">
            {items.map((row, i) => {
              const si = getSubjIcon(row.subject_name);
              return (
                <div key={i} className="vk-grid-card"
                  style={{ borderTopColor: accent, borderTopWidth: 3, borderTopStyle: "solid" }}
                  onClick={() => navigate(`${row.link}?api=${encodeURIComponent(row.api_link)}`)}>
                  <div className="vk-grid-icon">{si.emoji}</div>
                  <div className="vk-grid-title" style={{ color: accent }}>{row.subject_name}</div>
                  <div className="vk-grid-sub">{row.subtitle}</div>
                  <div className="vk-grid-teacher">
                    <img src={`/resources/${row.teacher_picture}`} alt={row.teacher_name}
                      onError={e => { e.target.style.display = "none"; }} />
                    {row.teacher_name}
                  </div>
                  <button className="vk-share-icon-btn"
                    title="Share"
                    style={{ alignSelf: "flex-end", marginTop: 2 }}
                    onClick={e => { e.stopPropagation(); setShareRow(row); }}>
                    📤
                  </button>
                </div>
              );
            })}
          </div>

        ) : (

          /* ── CARDS (default) ── */
          <div className="vk-cards-wrap">
            {items.map((row, i) => {
              const si = getSubjIcon(row.subject_name);
              return (
                <div key={i} className="vk-card"
                  style={{ borderTopColor: accent }}
                  onClick={() => navigate(`${row.link}?api=${encodeURIComponent(row.api_link)}`)}>
                  <div className="vk-card-top">
                    <div className="vk-card-icon" style={{ background: si.bg }}>{si.emoji}</div>
                    <div>
                      <div className="vk-card-title" style={{ color: accent }}>{row.subject_name}</div>
                      <div className="vk-card-sub">{row.subtitle}</div>
                    </div>
                  </div>
                  <div className="vk-card-footer">
                    <div className="vk-card-teacher">
                      <img src={`/resources/${row.teacher_picture}`} alt={row.teacher_name}
                        onError={e => { e.target.style.display = "none"; }} />
                      <span className="vk-card-teacher-name">{row.teacher_name}</span>
                    </div>
                    <button className="vk-share-icon-btn"
                      title="Share"
                      onClick={e => { e.stopPropagation(); setShareRow(row); }}>
                      📤
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div className={`vk-root${darkMode ? " vk-dark" : ""}`} style={{ fontSize }}>

        {/* ══ HERO ══ */}
        <div className="vk-hero">
          <div className="vk-hero-top">
            <div className="vk-logo">
              <div className="vk-logo-icon">📚</div>
              <div>
                <div className="vk-logo-text">{SITE_NAME}</div>
                <div className="vk-logo-sub">விர்ச்சுவல்கல்வி</div>
              </div>
            </div>
            <div className="vk-hero-date-pill">📅 {dateStr} · {tamilDay}</div>
          </div>

          <div className="vk-hero-body">
            <div className="vk-hero-tagline">அறிவு தான் சமத்துவத்தின் அடித்தளம் 🌟</div>
            <div className="vk-hero-sub" style={{ fontFamily:"'Noto Sans Tamil',sans-serif", fontSize:"0.92em", marginBottom:2 }}>
              தமிழ் &amp; ஆங்கிலத்தில் இலவச கல்வி — எங்கிருந்தும், எப்போதும்!
            </div>
            <div className="vk-hero-sub">Free education in Tamil &amp; English — anytime, anywhere!</div>

            <div className="vk-stats-row">
              <div className="vk-stat">
                <div className="vk-stat-num">{(rows || []).length}</div>
                <div className="vk-stat-lbl">Subjects</div>
              </div>
              <div className="vk-stat">
                <div className="vk-stat-num">{grades.length - 1}</div>
                <div className="vk-stat-lbl">Grades</div>
              </div>
              <div className="vk-stat">
                <div className="vk-stat-num">📖</div>
                <div className="vk-stat-lbl">Free to Learn</div>
              </div>
            </div>
          </div>

          {/* Wave bottom edge */}
          <div className="vk-hero-wave">
            <svg viewBox="0 0 1200 40" preserveAspectRatio="none">
              <path d="M0,40 C300,0 900,40 1200,10 L1200,40 Z" fill="#f1f5f9"/>
            </svg>
          </div>
        </div>

        {/* ══ ABOUT / DESCRIPTION ══ */}
        <div className="vk-about">
          <div className="vk-about-ta">
            📖 <strong>VirtualKalvi</strong> என்பது தமிழ்நாட்டு மாணவர்களுக்காக உருவாக்கப்பட்ட
            ஒரு இலவச டிஜிட்டல் கல்வி தளம். இங்கே <strong>தமிழ் மற்றும் ஆங்கிலம்</strong> இரண்டு
            மொழிகளிலும் கேள்விகள், பதில்கள் மற்றும் விளக்கங்கள் கிடைக்கும்.
            ஆசிரியர்கள் தங்கள் மாணவர்களுக்கு <strong>WhatsApp மூலம் தேர்வு அனுப்பலாம்</strong>,
            முக்கியமான கேள்விகளை பகிரலாம், மற்றும் மாணவர்களின் முன்னேற்றத்தை கண்காணிக்கலாம்.
            பெற்றோர்களும் இந்த தளத்தை பயன்படுத்தி தங்கள் பிள்ளைகளுக்கு வீட்டிலேயே பயிற்சி
            அளிக்கலாம் — எந்த கட்டணமும் இல்லாமல்! 🎓
          </div>
          <div className="vk-about-en">
            VirtualKalvi is a free digital learning platform designed for Tamil Nadu students.
            It offers questions, answers, and explanations in both <strong>Tamil and English</strong>.
            Teachers can send tests via WhatsApp, share important questions, and track student progress.
            Parents can also use this platform to help their children practise at home — completely free!
          </div>
        </div>

        {/* ══ FEATURE CARDS ══ */}
        <div className="vk-features-title">✨ நம் சேவைகள் — Our Features</div>
        <div className="vk-features-grid">

          {/* 1 — Two Mark Q&A */}
          <div className="vk-feat-card" style={{ borderTopColor:"#2563eb" }}>
            <div className="vk-feat-icon" style={{ background:"#dbeafe" }}>📝</div>
            <div className="vk-feat-ta">இரு மதிப்பெண் கேள்வி பதில்கள்</div>
            <div className="vk-feat-en">
              Two-Mark Q&amp;A in Tamil &amp; English side by side. Read aloud with one tap.
              Flashcard mode, bookmarks, and slide images included.
            </div>
            <span className="vk-feat-badge" style={{ background:"#dbeafe", color:"#1d4ed8" }}>📢 Tamil + English TTS</span>
          </div>

          {/* 2 — One Word MCQ */}
          <div className="vk-feat-card" style={{ borderTopColor:"#7c3aed" }}>
            <div className="vk-feat-icon" style={{ background:"#ede9fe" }}>🎯</div>
            <div className="vk-feat-ta">ஒரு வார்த்தை MCQ பயிற்சி</div>
            <div className="vk-feat-en">
              Multiple-choice one-word questions with instant answer feedback.
              Voice answer recognition — speak your answer and get checked!
            </div>
            <span className="vk-feat-badge" style={{ background:"#ede9fe", color:"#6d28d9" }}>🎤 குரல் பதில் சரிபார்ப்பு</span>
          </div>

          {/* 3 — Tamil Learning Slides */}
          <div className="vk-feat-card" style={{ borderTopColor:"#d97706" }}>
            <div className="vk-feat-icon" style={{ background:"#fef3c7" }}>📚</div>
            <div className="vk-feat-ta">தமிழ் கற்றல் — குரல் மூலம்</div>
            <div className="vk-feat-en">
              Learn Tamil letters and words by speaking aloud. Tracks every attempt,
              gives a full report card with WhatsApp share at the end.
            </div>
            <span className="vk-feat-badge" style={{ background:"#fef3c7", color:"#92400e" }}>📊 அறிக்கை அட்டை</span>
          </div>

          {/* 4 — Mock Test */}
          <div className="vk-feat-card" style={{ borderTopColor:"#16a34a" }}>
            <div className="vk-feat-icon" style={{ background:"#dcfce7" }}>🏆</div>
            <div className="vk-feat-ta">மாதிரி தேர்வு — WhatsApp வழியாக</div>
            <div className="vk-feat-en">
              Teachers create and send timed tests via WhatsApp link.
              Students take the test on their phone — results auto-reported to teacher.
            </div>
            <span className="vk-feat-badge" style={{ background:"#dcfce7", color:"#166534" }}>👩‍🏫 ஆசிரியர் / பெற்றோர்</span>
          </div>

          {/* 5 — Pick & Share */}
          <div className="vk-feat-card" style={{ borderTopColor:"#e55c3b" }}>
            <div className="vk-feat-icon" style={{ background:"#fff0eb" }}>📌</div>
            <div className="vk-feat-ta">கேள்விகள் தேர்வு செய்து பகிர்</div>
            <div className="vk-feat-en">
              Pick up to 10 important questions and share them instantly via WhatsApp.
              Friends open the link and practise those exact questions.
            </div>
            <span className="vk-feat-badge" style={{ background:"#fff0eb", color:"#c2410c" }}>💬 WhatsApp பகிர்வு</span>
          </div>

          {/* 6 — Progress & Bookmarks */}
          <div className="vk-feat-card" style={{ borderTopColor:"#0d9488" }}>
            <div className="vk-feat-icon" style={{ background:"#ccfbf1" }}>🔖</div>
            <div className="vk-feat-ta">புக்மார்க் &amp; முன்னேற்றம்</div>
            <div className="vk-feat-en">
              Bookmark important questions for later revision. Track accuracy,
              time spent, and words to practise — all saved on your device.
            </div>
            <span className="vk-feat-badge" style={{ background:"#ccfbf1", color:"#0f766e" }}>📈 முன்னேற்ற அட்டை</span>
          </div>

        </div>

        {/* ══ STUDENT SECTION ══ */}
        <div className="vk-students-wrap">
          <div className="vk-students-title">
            🎓 நம் <span>மாணவர்கள்</span> பற்றி — Our Students
          </div>
          <div className="vk-students-row">

            <div className="vk-student-card">
              <img src="/resources/Student1.png" alt="student" className="vk-student-img" />
              <div className="vk-student-name">பாவித்ரா</div>
              <div className="vk-student-quote">"தினமும் படிக்கிறேன், வெற்றி பெறுவேன்!"</div>
            </div>

            <div className="vk-student-card">
              <img src="/resources/Student2.png" alt="student" className="vk-student-img" />
              <div className="vk-student-name">தேவி</div>
              <div className="vk-student-quote">"VirtualKalvi என் தமிழ் திறனை வளர்த்தது!"</div>
            </div>

            <div className="vk-student-card">
              <img src="/resources/Student3.png" alt="student" className="vk-student-img" />
              <div className="vk-student-name">பிரபாகரன்</div>
              <div className="vk-student-quote">"ஆசிரியர் WhatsApp-ல் தேர்வு அனுப்புவார்!"</div>
            </div>

            <div className="vk-student-card">
              <img src="/resources/Student4.png" alt="student" className="vk-student-img" />
              <div className="vk-student-name">கார்த்திக்</div>
              <div className="vk-student-quote">"இலவசமாக படிக்கலாம், சிறப்பாக வாழலாம்!"</div>
            </div>

          </div>
        </div>

        {/* ══ SOCIAL STRIP ══ */}
        <div className="vk-social-strip">
          <div className="vk-social-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
            </svg>
          </div>
          <div className="vk-social-text">
            <div className="vk-social-title">Follow us on Facebook</div>
            <div className="vk-social-sub">Get updates, Q&amp;A cards and study tips</div>
          </div>
          <button className="vk-btn-fb" onClick={() => window.open(FB_PAGE_URL, "_blank")}>
            👍 Follow
          </button>
          <button className="vk-btn-msg" onClick={() => window.open(`https://m.me/${FB_PAGE_ID}`, "_blank")}>
            💬 Message
          </button>
        </div>

        {/* ══ TOOLBAR ══ */}
        <div className="vk-toolbar">
          <button className="vk-tb-btn"
            style={{ background: darkMode ? "#1e3050" : "#dde6f0", color: darkMode ? "#c8d8ec" : "#1a2e4a" }}
            onClick={() => setDarkMode(d => !d)}>
            {darkMode ? "☀️ Light" : "🌙 Dark"}
          </button>
          <button className="vk-tb-btn" style={{ background: "#dde6f0", color: "#1a2e4a" }}
            onClick={() => setFontSize(f => Math.min(f + 1, 20))}>A+</button>
          <button className="vk-tb-btn" style={{ background: "#dde6f0", color: "#475569" }}
            onClick={() => setFontSize(f => Math.max(f - 1, 11))}>A−</button>
          <button className="vk-tb-btn" style={{ background: "#dbeafe", color: "#1d4ed8" }}
            onClick={() => setShowContact(true)}>✉️ Contact</button>

          {/* ── NEW: Send Mock Test ── */}
          <button className="vk-tb-btn" style={{ background: "#dcfce7", color: "#16a34a" }}
            onClick={() => navigate("/sendtest")}>📤 Send Test</button>

          <div className="vk-view-group">
            {["list", "grid", "cards"].map(v => (
              <button key={v} className={`vk-view-btn${view === v ? " active" : ""}`}
                onClick={() => setView(v)}>
                {v === "list" ? "☰ List" : v === "grid" ? "⊞ Grid" : "⊟ Cards"}
              </button>
            ))}
          </div>
        </div>

        {/* ══ GRADE TABS ══ */}
        {grades.length > 2 && (
          <div className="vk-tabs-wrap">
            <div className="vk-tabs">
              {grades.map(g => (
                <button key={g} className={`vk-tab${activeGrade === g ? " active" : ""}`}
                  onClick={() => setActiveGrade(g)}>
                  {g === "All" ? "All Grades" : g}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ══ GRADE SECTIONS ══ */}
        {renderGrades.length === 0
          ? <div className="vk-empty">😕 No subjects available.</div>
          : renderGrades.map((g, i) => renderSection(g, i))
        }

        {/* ══ FOOTER ══ */}
        <div className="vk-footer">
          🎓 {SITE_NAME} &mdash; Happy learning! ✨ &nbsp;·&nbsp;
          <span className="vk-footer-link" onClick={() => window.open(FB_PAGE_URL, "_blank")}>
            Facebook
          </span>
          &nbsp;·&nbsp; © {now.getFullYear()}
        </div>

        {/* ══ SHARE POPUP ══ */}
        {shareRow && (
          <div className="vk-share-overlay" onClick={() => setShareRow(null)}>
            <div className="vk-share-box" onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div className="vk-share-title">📤 Share — {shareRow.subject_name}</div>
                <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}
                  onClick={() => setShareRow(null)}>✕</button>
              </div>
              <div className="vk-share-preview">
                📚 {shareRow.grade_text} — {shareRow.subject_name}<br />
                <span style={{ color: "#6b7280" }}>{shareRow.subtitle}</span><br />
                👩‍🏫 {shareRow.teacher_name}
              </div>
              <div className="vk-share-btns">
                <button className="vk-share-btn" onClick={() => doShare(shareRow, "wa")}>
                  <span className="vk-share-btn-icon">📲</span>WhatsApp
                </button>
                <button className="vk-share-btn" style={{ color: "#1877F2" }} onClick={() => doShare(shareRow, "fb")}>
                  <span className="vk-share-btn-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                    </svg>
                  </span>Facebook
                </button>
                <button className="vk-share-btn" onClick={() => doShare(shareRow, "ig")}>
                  <span className="vk-share-btn-icon">📸</span>Instagram
                </button>
              </div>
              {/* Always show — falls back to WhatsApp on non-iOS/Android */}
              <button style={{
                width: "100%", marginTop: 10,
                background: "linear-gradient(135deg, #1a2e4a, #2563eb)",
                color: "#fff", border: "none", borderRadius: 50,
                padding: "14px",   /* bigger tap target for iOS */
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                fontFamily: "Baloo 2,sans-serif",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
              }} onClick={() => doShare(shareRow, "native")}>
                📱 {navigator.share ? "Share via iPhone / Android" : "Share via WhatsApp"}
              </button>
            </div>
          </div>
        )}

        {/* ══ CONTACT MODAL ══ */}
        {showContact && (
          <div className="vk-modal-overlay" onClick={() => setShowContact(false)}>
            <div className="vk-modal" onClick={e => e.stopPropagation()}>
              <div className="vk-modal-head">
                <div className="vk-modal-title">✉️ Contact Us</div>
                <button className="vk-modal-close" onClick={() => setShowContact(false)}>✕</button>
              </div>
              <input className="vk-input" type="text"
                placeholder="Subject" value={mailSubject}
                onChange={e => setMailSubject(e.target.value)} />
              <textarea className="vk-input" rows="4"
                style={{ resize: "vertical" }}
                placeholder="Your message…" value={mailBody}
                onChange={e => setMailBody(e.target.value)} />
              <div className="vk-modal-actions">
                <button className="vk-btn-cancel" onClick={() => setShowContact(false)}>Cancel</button>
                <button className="vk-btn-send" onClick={handleSendMail}>Send 🚀</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
